import { getProjectIndicator } from "../mock/allocationBaseMock";

export const BASE_METHODS = [
  {
    id: "equal",
    title: "Равномерно",
    shortTitle: "Равномерно",
    description: "Разделить сумму поровну между выбранными проектами.",
  },
  {
    id: "opiu",
    title: "По статьям ОПиУ",
    shortTitle: "ОПиУ",
    description: "Рассчитать доли проектов на основании показателей ОПиУ.",
    articlesLabel: "Статьи ОПиУ",
  },
  {
    id: "dds",
    title: "По статьям ДДС",
    shortTitle: "ДДС",
    description: "Рассчитать доли на основании показателей ДДС.",
    articlesLabel: "Статьи ДДС",
  },
  {
    id: "balance",
    title: "По статьям Баланса",
    shortTitle: "Баланс",
    description: "Рассчитать доли на основании показателей Баланса.",
    articlesLabel: "Статьи Баланса",
  },
  {
    id: "manual",
    title: "Произвольно",
    shortTitle: "Произвольно",
    description: "Указать долю каждого проекта вручную.",
  },
];

export const BASE_METHOD_BY_ID = Object.fromEntries(
  BASE_METHODS.map((method) => [method.id, method])
);

export const EMPTY_BASE = {
  method: "equal",
  articles: { opiu: [], dds: [], balance: [] },
  manualShares: {},
};

export const isArticleMethod = (method) => Boolean(BASE_METHOD_BY_ID[method]?.articlesLabel);

/** "33,5" → 33.5; пустое или некорректное значение — 0 */
export const parsePercent = (text) => {
  const value = Number(String(text ?? "").replace(",", ".").replace(/\s/g, ""));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/** Равные доли в процентах с двумя знаками; остаток округления — последнему проекту */
export const getEqualPercents = (projects) => {
  if (!projects.length) return {};
  const base = Math.floor(10000 / projects.length) / 100;
  const result = {};
  projects.forEach((project, index) => {
    const isLast = index === projects.length - 1;
    const value = isLast ? 100 - base * (projects.length - 1) : base;
    result[project.id] = String(Math.round(value * 100) / 100).replace(".", ",");
  });
  return result;
};

/** Суммы по долям в целых тенге; остаток округления уходит проекту с наибольшей долей */
export const distributeAmount = (total, shares) => {
  const amounts = shares.map((share) => Math.floor(total * share));
  const remainder = total - amounts.reduce((sum, amount) => sum + amount, 0);
  if (remainder !== 0 && shares.length) {
    const maxIndex = shares.indexOf(Math.max(...shares));
    amounts[maxIndex] += remainder;
  }
  return amounts;
};

/**
 * Предварительное распределение суммы между проектами.
 * Возвращает { rows: [{ project, base, share, amount }], totalShare, error }.
 * error — причина, по которой распределение посчитать нельзя (кнопка «Продолжить» неактивна).
 */
export const calculateAllocation = (projects, total, base) => {
  const { method } = base;
  if (!projects.length) {
    return { rows: [], totalShare: 0, error: "Не выбраны проекты-получатели" };
  }

  let weights;
  let bases = null;
  let error = null;

  if (method === "equal") {
    weights = projects.map(() => 1);
  } else if (method === "manual") {
    weights = projects.map((project) => parsePercent(base.manualShares[project.id]));
    const sum = weights.reduce((acc, value) => acc + value, 0);
    if (Math.abs(sum - 100) > 0.001) {
      const current = sum.toLocaleString("ru-RU", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
      error = `Для продолжения сумма долей должна быть равна 100%. Текущий итог: ${current}%`;
    }
  } else {
    const articles = base.articles[method];
    if (!articles.length) {
      return { rows: [], totalShare: 0, error: "Выберите хотя бы одну статью" };
    }
    bases = projects.map((project) =>
      articles.reduce(
        (sum, article) => sum + getProjectIndicator(project.id, method, article),
        0
      )
    );
    weights = bases;
    if (bases.every((value) => value === 0)) {
      const usesServerBase = projects.some((project) => typeof project.id === "string");
      if (!usesServerBase) {
        error = "По выбранным статьям у проектов нет показателей";
      }
    }
  }

  const weightSum = weights.reduce((acc, value) => acc + value, 0);
  const shares = weights.map((value) => (weightSum > 0 ? value / weightSum : 0));
  const manualShares = method === "manual" ? weights.map((value) => value / 100) : null;
  const displayShares = manualShares || shares;
  const amounts = error
    ? displayShares.map((share) => Math.round(total * share))
    : distributeAmount(total, shares);

  return {
    rows: projects.map((project, index) => ({
      project,
      base: bases ? bases[index] : null,
      share: displayShares[index],
      amount: amounts[index],
    })),
    totalShare: displayShares.reduce((acc, value) => acc + value, 0),
    error,
  };
};

export const formatPercent = (share) =>
  `${(Math.round(share * 10000) / 100).toLocaleString("ru-RU", {
    maximumFractionDigits: 2,
  })} %`;

/** «Равномерно» или «По статьям ОПиУ: Субподряд, Материалы» */
export const getBaseLabel = (base) => {
  const method = BASE_METHOD_BY_ID[base.method];
  if (!isArticleMethod(base.method)) return method.title;
  return `${method.title}: ${base.articles[base.method].join(", ")}`;
};
